import React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

import { Page } from '../components';
import { jarvisApi } from '../api';
import { logger } from '../logging';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`runtime-tabpanel-${index}`}
      aria-labelledby={`runtime-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

interface OrchestrationStatus {
  instanceId: string;
  name: string;
  runtimeStatus: string;
  customStatus: string;
  createdTime: string;
  lastUpdatedTime: string;
  completedTime?: string;
  correlationId?: string;
  filename?: string;
  containerName?: string;
  batchStartTime?: string;
  emailRecipients?: string;
  functionName?: string;
  fileSize?: number;
  exceptionMessage?: string;
  errorDetails?: string;
}

interface FileGroup {
  correlationId: string;
  filename?: string;
  containerName: string;
  orchestrations: OrchestrationStatus[];
  oldestStartTime: string;
  latestUpdateTime: string;
  emailRecipients?: string;
  functionName?: string;
}

export const RuntimeHub = () => <Page maxWidth="xl" render={() => <Content />} />;

const Content = () => {
  const [tabValue, setTabValue] = React.useState(0);
  const [runningOrchestrations, setRunningOrchestrations] = React.useState<OrchestrationStatus[]>([]);
  const [completedOrchestrations, setCompletedOrchestrations] = React.useState<OrchestrationStatus[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedFiles, setExpandedFiles] = React.useState<Set<string>>(new Set());
  const [errorDialogOpen, setErrorDialogOpen] = React.useState(false);
  const [selectedErrorDetails, setSelectedErrorDetails] = React.useState<{ message: string; details: string } | null>(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [running, completed] = await Promise.all([
        jarvisApi.getRuntimeHub('running'),
        jarvisApi.getRuntimeHub('completed'), // Last 5 days of completed
      ]);

      // Ensure we have arrays
      const runningArray = Array.isArray(running) ? running : [];
      const completedArray = Array.isArray(completed) ? completed : [];

      setRunningOrchestrations(runningArray);
      setCompletedOrchestrations(completedArray);
    } catch (e: any) {
      logger.error('Failed to fetch runtime hub data', e);
      setError('Failed to load runtime data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();

    // Auto-refresh running tab every 30 seconds
    const interval = setInterval(() => {
      if (tabValue === 0) {
        fetchData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchData, tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    if (newValue === 1) {
      // Refresh completed when switching to that tab
      fetchData();
    }
  };

  const toggleFileExpansion = (correlationId: string) => {
    setExpandedFiles((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(correlationId)) {
        newSet.delete(correlationId);
      } else {
        newSet.add(correlationId);
      }
      return newSet;
    });
  };

  const handleErrorClick = (message: string, details?: string) => {
    if (details) {
      setSelectedErrorDetails({ message, details });
      setErrorDialogOpen(true);
    }
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setSelectedErrorDetails(null);
  };

  const groupByFilename = (orchestrations: OrchestrationStatus[]): FileGroup[] => {
    const groups = new Map<string, FileGroup>();

    orchestrations.forEach((orch) => {
      const key = orch.correlationId || orch.instanceId; // Group by correlationId, fallback to instanceId
      if (!groups.has(key)) {
        groups.set(key, {
          correlationId: key,
          filename: orch.filename || 'Unknown',
          containerName: orch.containerName || '',
          orchestrations: [],
          oldestStartTime: orch.batchStartTime || orch.createdTime,
          latestUpdateTime: orch.lastUpdatedTime,
          emailRecipients: orch.emailRecipients,
          functionName: orch.functionName,
        });
      }
      const group = groups.get(key)!;
      group.orchestrations.push(orch);

      // Update times
      if (orch.batchStartTime && orch.batchStartTime < group.oldestStartTime) {
        group.oldestStartTime = orch.batchStartTime;
      }
      if (orch.lastUpdatedTime > group.latestUpdateTime) {
        group.latestUpdateTime = orch.lastUpdatedTime;
      }
    });

    return Array.from(groups.values()).sort(
      (a, b) => new Date(b.latestUpdateTime).getTime() - new Date(a.latestUpdateTime).getTime()
    );
  };

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'just now';
  };

  const formatTimestamp = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'terminated':
        return 'error';
      default:
        return 'default';
    }
  };

  const ErrorMessageCell = ({ message, details }: { message?: string; details?: string }) => {
    if (!message) return <span>-</span>;

    if (details) {
      return (
        <Link
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            handleErrorClick(message, details);
          }}
          sx={{ fontSize: '0.75rem', color: 'error.main', textAlign: 'left', cursor: 'pointer' }}
        >
          {message}
        </Link>
      );
    }

    return <span style={{ fontSize: '0.75rem', color: 'inherit' }}>{message}</span>;
  };

  const runningGroups = groupByFilename(runningOrchestrations);
  const completedGroups = groupByFilename(completedOrchestrations);
  const distinctRunningFiles = runningGroups.length;
  const totalRunningOrchestrations = runningOrchestrations.length;
  const actuallyRunningCount = runningOrchestrations.filter(o => o.runtimeStatus === 'Running').length;

  if (loading && runningOrchestrations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
            Runtime Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor the status of all rating request orchestrations
          </Typography>
        </Box>
        <IconButton onClick={fetchData} color="primary" disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Progress Context */}
      {distinctRunningFiles > 0 && (
        <Card sx={{ mb: 3, backgroundColor: '#e3f2fd' }}>
          <CardContent>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              {distinctRunningFiles} request{distinctRunningFiles !== 1 ? 's' : ''} processing
              {totalRunningOrchestrations !== distinctRunningFiles && (
                <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                  ({totalRunningOrchestrations} orchestration{totalRunningOrchestrations !== 1 ? 's' : ''} running)
                </Typography>
              )}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Running
                {actuallyRunningCount > 0 && (
                  <Chip label={actuallyRunningCount} size="small" color="primary" />
                )}
              </Box>
            }
          />
          <Tab label="Recent History" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {runningGroups.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: 'center' }} color="text.secondary">
            No requests currently running
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>File Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Custom Status</TableCell>
                  <TableCell>Orchestrations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runningGroups.map((group) => {
                  const isExpanded = expandedFiles.has(group.correlationId);
                  const statuses = group.orchestrations.map(o => o.runtimeStatus);
                  // Priority: Failed > Terminated > Running > others
                  const hasFailed = statuses.includes('Failed');
                  const hasTerminated = statuses.includes('Terminated');
                  const hasRunning = statuses.includes('Running');
                  const summaryStatus = hasFailed ? 'Failed' : hasTerminated ? 'Terminated' : hasRunning ? 'Running' : statuses[0];
                  const latestCustomStatus = group.orchestrations
                    .filter(o => o.customStatus)
                    .sort((a, b) => new Date(b.lastUpdatedTime).getTime() - new Date(a.lastUpdatedTime).getTime())[0]?.customStatus || '';

                  return (
                    <React.Fragment key={group.correlationId}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleFileExpansion(group.correlationId)}>
                        <TableCell>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{group.filename || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip label={summaryStatus} size="small" color={getStatusColor(summaryStatus)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{latestCustomStatus || '-'}</TableCell>
                        <TableCell>
                          <Chip label={group.orchestrations.length} size="small" color="info" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Instance ID</TableCell>
                                    <TableCell>Function</TableCell>
                                    <TableCell>Last Updated</TableCell>
                                    <TableCell>Error Message</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.orchestrations.map((orch) => (
                                    <TableRow key={orch.instanceId || Math.random()}>
                                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        {orch.instanceId ? `${orch.instanceId.substring(0, 16)}...` : 'N/A'}
                                      </TableCell>
                                      <TableCell>{orch.functionName || orch.name}</TableCell>
                                      <TableCell>{formatTimestamp(orch.lastUpdatedTime)}</TableCell>
                                      <TableCell>
                                        <ErrorMessageCell message={orch.exceptionMessage} details={orch.errorDetails} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {completedGroups.length === 0 ? (
          <Typography sx={{ p: 4, textAlign: 'center' }} color="text.secondary">
            No completed requests found
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>File Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Custom Status</TableCell>
                  <TableCell>Orchestrations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {completedGroups.map((group) => {
                  const isExpanded = expandedFiles.has(group.correlationId);
                  const statuses = group.orchestrations.map(o => o.runtimeStatus);
                  // Priority: Failed > Terminated > Completed
                  const hasFailed = statuses.includes('Failed');
                  const hasTerminated = statuses.includes('Terminated');
                  const summaryStatus = hasFailed ? 'Failed' : hasTerminated ? 'Terminated' : 'Completed';
                  const latestCustomStatus = group.orchestrations
                    .filter(o => o.customStatus)
                    .sort((a, b) => new Date(b.lastUpdatedTime).getTime() - new Date(a.lastUpdatedTime).getTime())[0]?.customStatus || '';

                  return (
                    <React.Fragment key={group.correlationId}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleFileExpansion(group.correlationId)}>
                        <TableCell>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{group.filename || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip label={summaryStatus} size="small" color={getStatusColor(summaryStatus)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.875rem' }}>{latestCustomStatus || '-'}</TableCell>
                        <TableCell>
                          <Chip label={group.orchestrations.length} size="small" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Instance ID</TableCell>
                                    <TableCell>Function</TableCell>
                                    <TableCell>Completed</TableCell>
                                    <TableCell>Error Message</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.orchestrations.map((orch) => (
                                    <TableRow key={orch.instanceId || Math.random()}>
                                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        {orch.instanceId ? `${orch.instanceId.substring(0, 16)}...` : 'N/A'}
                                      </TableCell>
                                      <TableCell>{orch.functionName || orch.name}</TableCell>
                                      <TableCell>{formatTimestamp(orch.completedTime)}</TableCell>
                                      <TableCell>
                                        <ErrorMessageCell message={orch.exceptionMessage} details={orch.errorDetails} />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Error Details Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Error Details</DialogTitle>
        <DialogContent>
          {selectedErrorDetails && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Message:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'error.main' }}>
                {selectedErrorDetails.message}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Stack Trace:
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  backgroundColor: '#f5f5f5',
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedErrorDetails.details}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
